import React, { useState } from 'react';
import { Package, Plus, Trash2, X } from 'lucide-react';
import { field, ghost } from '../purchasing/ui';
import { jobPrice } from './labourMath';

const PackageCard = ({ p, jobs, segs, onChange, onDelete }) => {
  const [add, setAdd] = useState('');
  const picked = p.job_ids.map((id) => jobs.find((j) => j.id === id)).filter(Boolean);
  const f = 1 - (Number(p.discount_pct) || 0) / 100;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`package-${p.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Package size={16} className="text-teal-700" />
        <input value={p.name} onChange={(e) => onChange({ name: e.target.value })} className="min-w-[180px] flex-1 rounded px-1 py-1 text-sm font-semibold hover:bg-slate-50" data-testid={`package-name-${p.id}`} />
        <label className="flex items-center gap-1 text-xs text-slate-500">Discount
          <input type="number" min="0" max="100" value={p.discount_pct ?? 0} onChange={(e) => onChange({ discount_pct: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })} className="w-16 rounded border px-1.5 py-1 text-right" data-testid={`package-discount-${p.id}`} />%
        </label>
        <button onClick={onDelete} className="text-red-500" data-testid={`package-delete-${p.id}`}><Trash2 size={15} /></button>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {picked.map((j) => (
          <span key={j.id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs" data-testid={`package-job-${p.id}-${j.id}`}>
            {j.name}<button onClick={() => onChange({ job_ids: p.job_ids.filter((x) => x !== j.id) })} data-testid={`package-job-remove-${p.id}-${j.id}`}><X size={12} /></button>
          </span>))}
        <select value={add} onChange={(e) => { if (e.target.value) onChange({ job_ids: [...p.job_ids, e.target.value] }); setAdd(''); }} className="rounded-full border px-2 py-1 text-xs" data-testid={`package-add-job-${p.id}`}>
          <option value="">+ Add job…</option>{jobs.filter((j) => !p.job_ids.includes(j.id)).map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
        </select>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        {segs.map((s) => {
          const ps = picked.map((j) => jobPrice(j, s));
          const sum = ps.reduce((a, x) => a + (x || 0), 0);
          const gap = ps.some((x) => x == null);
          return (
            <span key={s.key} className={`rounded-lg border px-2 py-1 ${gap ? 'border-amber-200 bg-amber-50 text-amber-800' : 'bg-slate-50'}`} title={gap ? 'Some jobs have no price for this size' : ''} data-testid={`package-price-${p.id}-${s.key}`}>
              <b>{s.key}</b> ₹{Math.round(sum * f).toLocaleString('en-IN')}{f < 1 && sum > 0 && <s className="ml-1 text-slate-400">₹{Math.round(sum).toLocaleString('en-IN')}</s>}
            </span>);
        })}
      </div>
    </div>
  );
};

export const PackagesPanel = ({ chart, setChart }) => {
  const packs = chart.packages || [];
  const set = (packages) => setChart({ ...chart, packages });
  return (
    <div className="space-y-2" data-testid="labour-packages">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-bold text-slate-900">Packages</h2>
        <p className="text-xs text-slate-500">Bundle jobs. Price = sum of the jobs for that vehicle size, less the discount. Bills show each job as its own line.</p>
        <button className={`${ghost} ml-auto`} onClick={() => set([{ id: `new-${Date.now()}`, name: 'New package', job_ids: [], discount_pct: 0 }, ...packs])} data-testid="package-add"><Plus size={14} /> Add package</button>
      </div>
      {!packs.length && <p className={`${field} text-slate-400`} data-testid="packages-empty">No packages yet, e.g. “Full service” = oil change + filters + wash.</p>}
      <div className="grid gap-2 lg:grid-cols-2">
        {packs.map((p, i) => (
          <PackageCard key={p.id} p={p} jobs={chart.jobs} segs={chart.segments}
            onChange={(patch) => set(packs.map((x, j) => (j === i ? { ...x, ...patch } : x)))} onDelete={() => set(packs.filter((_, j) => j !== i))} />))}
      </div>
    </div>
  );
};
